import React, { useState, useRef, useEffect } from 'react';
import { Plus, Calendar, Check, UserPlus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n';
import clsx from 'clsx';
import type { Season } from '../../lib/seasonsApi';
import SeasonIcon from '../SeasonIcon';

const SHOW_SEASON_SWITCHER = true;

interface QuickActionProps {
    collapsed?: boolean;
    seasons?: Season[];
    selectedSeasonId?: string | null;
    onSelectSeason?: (seasonId: string) => void;
}

const QuickAction: React.FC<QuickActionProps> = ({
    collapsed,
    seasons = [],
    selectedSeasonId = null,
    onSelectSeason,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { t } = useI18n(); // Assuming we might add translations later, for now using raw strings in menu or t if keys exist

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleAction = (path: string) => {
        navigate(path);
        setIsOpen(false);
    };

    const handleSeason = (seasonId: string) => {
        onSelectSeason?.(seasonId);
        setIsOpen(false);
    };

    return (
        <div className="relative px-2 mb-6" ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={clsx(
                    'flex items-center w-full bg-primary-600 hover:bg-primary-700 text-white transition-all shadow-md',
                    collapsed ? 'justify-center h-10 w-10 rounded-full mx-auto' : 'justify-start rounded-lg py-2 px-4'
                )}
                title={t('quick_action_title')}
            >
                {isOpen && collapsed ? <X size={20} /> : <Plus size={20} className={clsx(!collapsed && 'mr-2')} />}
                {!collapsed && <span className="font-semibold">{t('quick_action_button')}</span>}
            </button>

            {isOpen && (
                <div className={clsx(
                    "absolute z-50 w-56 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200",
                    collapsed ? "left-14 top-0" : "left-0 top-12"
                )}>
                    <div className="py-1">
                        <button
                            onClick={() => handleAction('/match-planning')}
                            className="w-full text-left px-4 py-3 flex items-center hover:bg-gray-50 transition-colors"
                        >
                            <div className="bg-gray-100 p-2 rounded-full mr-3 text-gray-700">
                                <Calendar size={18} />
                            </div>
                            <div>
                                <div className="text-sm font-medium text-gray-900">{t('quick_action_plan_match')}</div>
                                {t('quick_action_plan_match_desc') && (
                                    <div className="text-xs text-gray-500">{t('quick_action_plan_match_desc')}</div>
                                )}
                            </div>
                        </button>

                        <button
                            onClick={() => handleAction('/players')}
                            className="w-full text-left px-4 py-3 flex items-center hover:bg-gray-50 transition-colors"
                        >
                            <div className="bg-gray-100 p-2 rounded-full mr-3 text-gray-700">
                                <UserPlus size={18} />
                            </div>
                            <div>
                                <div className="text-sm font-medium text-gray-900">{t('quick_action_add_player')}</div>
                                {t('quick_action_add_player_desc') && (
                                    <div className="text-xs text-gray-500">{t('quick_action_add_player_desc')}</div>
                                )}
                            </div>
                        </button>

                        {SHOW_SEASON_SWITCHER && seasons.length > 0 && (
                            <div className="border-t border-gray-200 py-1">
                                <div className="flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    <SeasonIcon size={14} />
                                    {t('quick_action_season')}
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                    {seasons.map(season => (
                                        <button
                                            key={season.id}
                                            type="button"
                                            onClick={() => handleSeason(season.id)}
                                            className="flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-gray-50"
                                        >
                                            <span className={clsx(
                                                'flex-grow text-sm',
                                                selectedSeasonId === season.id ? 'font-semibold text-primary-700' : 'text-gray-700'
                                            )}>
                                                {season.name}
                                                <span className="ml-2 text-xs font-normal text-gray-500">
                                                    {t(`seasons_status_${season.status}`)}
                                                </span>
                                            </span>
                                            {selectedSeasonId === season.id && <Check size={16} className="flex-shrink-0 text-primary-600" />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuickAction;
